import React, { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useResourcesConfig } from '../config/ResourcesConfigContext';
import { useResourcesLibrary } from '../providers/resourcesLibraryProvider';
import DeleteResourceModal from './DeleteResourceModal';
import ResourceFormModal from './ResourceFormModal';
import {
    AUTHORING_LABEL,
    createDraft,
    draftFromItem,
    type AuthorableItem,
    type AuthoringKind,
    type ResourceDraft
} from './types';

interface AuthoringContextValue {
    /** Holder of `resources.update`: the pages show their New and Edit controls. */
    canEdit: boolean;
    /** Holder of `resources.delete`: the form offers Delete. */
    canDelete: boolean;
    startCreate: (kind: AuthoringKind) => void;
    startEdit: (kind: AuthoringKind, item: AuthorableItem) => void;
}

const READ_ONLY: AuthoringContextValue = {
    canEdit: false,
    canDelete: false,
    startCreate: () => undefined,
    startEdit: () => undefined
};

const AuthoringContext = createContext<AuthoringContextValue>(READ_ONLY);

/** Categories already in use for a kind, so the form can offer them instead of
 * inviting a near-duplicate. Derived from the lists the shell already loaded. */
function useCategorySuggestions(kind: AuthoringKind | null): string[] {
    const { faqItems, helpDocuments, videos } = useResourcesLibrary();

    return useMemo(() => {
        if (kind === null) return [];
        const source = kind === 'faq' ? faqItems : kind === 'helpDocument' ? helpDocuments : videos;
        const seen = new Set<string>();
        for (const item of source) {
            for (const category of item.category) seen.add(category);
        }
        return [...seen].sort((a, b) => a.localeCompare(b));
    }, [kind, faqItems, helpDocuments, videos]);
}

/**
 * Owns the authoring flow for the whole section: the open draft, the form, and
 * the delete confirmation. Mounted once by `ResourcesShell`, so a page only has
 * to render a button and say what it wants edited.
 *
 * Hosts that grant nothing get `READ_ONLY` through the default context value and
 * never mount a modal.
 */
export const ResourceAuthoringProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { authoring } = useResourcesConfig();
    const { refresh } = useResourcesLibrary();
    const [draft, setDraft] = useState<ResourceDraft | null>(null);
    const [pendingDelete, setPendingDelete] = useState<ResourceDraft | null>(null);
    const suggestions = useCategorySuggestions(draft?.kind ?? null);

    const startCreate = useCallback((kind: AuthoringKind) => setDraft(createDraft(kind)), []);
    const startEdit = useCallback(
        (kind: AuthoringKind, item: AuthorableItem) => setDraft(draftFromItem(kind, item)),
        []
    );

    const value = useMemo<AuthoringContextValue>(
        () => ({
            canEdit: authoring.canEdit,
            canDelete: authoring.canDelete,
            startCreate,
            startEdit
        }),
        [authoring.canEdit, authoring.canDelete, startCreate, startEdit]
    );

    // A write changes what every page in the section shows, and the lists are
    // loaded once for the whole section - so a save forces a reload of all three
    // rather than patching one page's state.
    const reload = useCallback(async () => {
        await refresh(true);
    }, [refresh]);

    return (
        <AuthoringContext.Provider value={value}>
            {children}
            {authoring.canEdit && draft && (
                <ResourceFormModal
                    draft={draft}
                    suggestions={suggestions}
                    onClose={() => setDraft(null)}
                    onSaved={reload}
                    onDelete={() => {
                        setPendingDelete(draft);
                        setDraft(null);
                    }}
                />
            )}
            {authoring.canDelete && pendingDelete?.id != null && (
                <DeleteResourceModal
                    kind={pendingDelete.kind}
                    id={pendingDelete.id}
                    label={AUTHORING_LABEL[pendingDelete.kind]}
                    name={
                        pendingDelete.kind === 'faq' ? pendingDelete.question : pendingDelete.title
                    }
                    onClose={() => setPendingDelete(null)}
                    onDeleted={reload}
                />
            )}
        </AuthoringContext.Provider>
    );
};

export function useResourceAuthoring(): AuthoringContextValue {
    return useContext(AuthoringContext);
}
